"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getPhysicalDimensions = exports.getPrintedImageDimensions = exports.getPrintedCanvasImageDimensions = exports.calculateWideBackgroundSize = void 0;
const tiles_1 = require("../consts/tiles");
const tiles_2 = require("./tiles");
const createJigs_consts_1 = require("../consts/createJigs.consts");
const bleed_1 = require("../consts/bleed");
const utils_1 = require("./utils");
/**
 * In wide frames, we add a border image (with a repeating pattern containing
 * the text "Wide"), to prevent mistakes of the employees in the factories - that
 * they won't put a regular frame for a wide image.
 *
 * Notice: a better solution would be to print a smaller image (that exactly fit the
 * wider frame), but it requires stocks of smaller printing papers and the
 * operations decided to print on the same papers during the release phase. If we
 * support smaller page sizes in the future we can remove the wide-background from
 * createJigs function and simply create a different mapping, like
 * F_LINE_FRAMED_TILE_SIZE_TO_JIG_PROPERTIES, with the smaller page sizes
 *
 *
 * This function calculates the size, in pixels, of this border image.
 * @return {number} - size in pixels of the width of the "Wide" border image
 */
function calculateWideBackgroundSize(tileSize) {
    const wideFrameSize = (0, tiles_2.getFrameWidth)({
        materialType: tiles_1.MATERIAL_TYPES.WIDE_FRAME,
        tileSize,
    });
    const regularFrameSize = (0, tiles_2.getFrameWidth)({
        materialType: tiles_1.MATERIAL_TYPES.CLASSIC,
        tileSize,
        isPlastic: false,
    });
    return wideFrameSize - regularFrameSize;
}
exports.calculateWideBackgroundSize = calculateWideBackgroundSize;
// Return final dimensions of printed canvas (including the wraps of the sides of the canvas)
function getPrintedCanvasImageDimensions(tileSize) {
    const { bleedWidth, bleedHeight } = (0, bleed_1.getBleed)({
        tileSize,
        materialType: tiles_1.MATERIAL_TYPES.CANVAS,
    });
    const { width, height } = tiles_1.CANVAS_FRONT_DIMENSIONS[tileSize];
    return {
        jigImageWidth: width + 2 * bleedWidth,
        jigImageHeight: height + 2 * bleedHeight,
    };
}
exports.getPrintedCanvasImageDimensions = getPrintedCanvasImageDimensions;
/**
 * Return the dimensions of the image that we send to the factories for printing (including the bleed).
 */
function getPrintedImageDimensions({ materialType, tileSize, isPlastic, }) {
    let jigImageWidth;
    let jigImageHeight;
    if (materialType === tiles_1.MATERIAL_TYPES.CANVAS) {
        const canvasDimensions = getPrintedCanvasImageDimensions(tileSize);
        jigImageWidth = canvasDimensions.jigImageWidth;
        jigImageHeight = canvasDimensions.jigImageHeight;
    }
    else if (materialType === tiles_1.MATERIAL_TYPES.FRAMELESS) {
        jigImageWidth = createJigs_consts_1.FRAMELESS_TILE_SIZE_TO_JIG_PROPERTIES[tileSize].tileWidth;
        jigImageHeight = createJigs_consts_1.FRAMELESS_TILE_SIZE_TO_JIG_PROPERTIES[tileSize].tileHeight;
    }
    else if (materialType === tiles_1.MATERIAL_TYPES.CLASSIC) {
        const jigProps = isPlastic ? createJigs_consts_1.FRAMED_TILE_SIZE_TO_JIG_PROPERTIES : createJigs_consts_1.F_LINE_FRAMED_TILE_SIZE_TO_JIG_PROPERTIES;
        jigImageWidth = jigProps[tileSize].tileWidth;
        jigImageHeight = jigProps[tileSize].tileHeight;
    }
    else if (materialType === tiles_1.MATERIAL_TYPES.WIDE_FRAME) {
        // Wide frames have a border with the text "Wide" written on it, so we need to reduce the image size
        const wideBackgroundSize = calculateWideBackgroundSize(tileSize);
        jigImageWidth = createJigs_consts_1.F_LINE_FRAMED_TILE_SIZE_TO_JIG_PROPERTIES[tileSize].tileWidth - 2 * wideBackgroundSize;
        jigImageHeight = createJigs_consts_1.F_LINE_FRAMED_TILE_SIZE_TO_JIG_PROPERTIES[tileSize].tileHeight - 2 * wideBackgroundSize;
    }
    else if (materialType === tiles_1.MATERIAL_TYPES.STICKER) {
        // Stickers are printed on a sheet of paper, so we don't need to add bleed
        jigImageWidth = tiles_1.CANVAS_FRONT_DIMENSIONS[tileSize].width;
        jigImageHeight = tiles_1.CANVAS_FRONT_DIMENSIONS[tileSize].height;
    }
    if (!jigImageWidth || !jigImageHeight) {
        throw new Error(`Failed getting image dimensions for tile size ${tileSize} for material type ${materialType}`);
    }
    jigImageWidth = (0, utils_1.roundDecimal)(jigImageWidth);
    jigImageHeight = (0, utils_1.roundDecimal)(jigImageHeight);
    return { jigImageWidth, jigImageHeight };
}
exports.getPrintedImageDimensions = getPrintedImageDimensions;
// Return the dimensions of the physical tile - it requires: taking the size of the printed image, removing the bleed and adding the frame width
function getPhysicalDimensions({ materialType, tileSize, isPlastic, }) {
    let physicalWidth;
    let physicalHeight;
    // 1. Take the printed image dimensions
    const printedImageDimensions = getPrintedImageDimensions({
        materialType,
        tileSize,
        isPlastic,
    });
    physicalWidth = printedImageDimensions.jigImageWidth;
    physicalHeight = printedImageDimensions.jigImageHeight;
    // 2. Remove the bleed
    const { bleedWidth, bleedHeight } = (0, bleed_1.getBleed)({
        tileSize,
        materialType,
        shouldAddBuffer: true,
        isPlastic,
    });
    physicalWidth -= 2 * bleedWidth;
    physicalHeight -= 2 * bleedHeight;
    // Add the frame width for framed materials
    if (materialType === tiles_1.MATERIAL_TYPES.CLASSIC || materialType === tiles_1.MATERIAL_TYPES.WIDE_FRAME) {
        const frameWidth = (0, tiles_2.getFrameWidth)({
            materialType,
            tileSize,
            isPlastic,
        });
        physicalWidth += 2 * frameWidth;
        physicalHeight += 2 * frameWidth;
    }
    physicalWidth = (0, utils_1.roundDecimal)(physicalWidth);
    physicalHeight = (0, utils_1.roundDecimal)(physicalHeight);
    return { physicalWidth, physicalHeight };
}
exports.getPhysicalDimensions = getPhysicalDimensions;
//# sourceMappingURL=createJigUtils.js.map